import { useQuery } from '@tanstack/vue-query';
import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';

export type UpcomingPresentation = {
  id: string;
  developerId: number;
  developerName: string;
  projectId: number | null;
  projectName: string | null;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  meetingLink: string | null;
  materials: { name: string; url: string }[];
};

// Guests get no header; the endpoint serves them too.
const optionalAuthHeaders = (): Record<string, string> => {
  const token = useAuthStore().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const useUpcomingPresentationsQuery = (
  developerId: MaybeRefOrGetter<number>,
  projectId: MaybeRefOrGetter<number | undefined>,
) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: computed(() => [
      'developer-presentations',
      'upcoming',
      toValue(developerId),
      toValue(projectId) ?? null,
      !!authStore.token,
    ]),
    queryFn: () => {
      const query: Record<string, number> = { developerId: toValue(developerId) };
      const pid = toValue(projectId);
      if (pid != null) query.projectId = pid;
      return $fetch<{ data: UpcomingPresentation[]; signedIn: boolean }>('/api/developer-presentations', {
        query,
        headers: optionalAuthHeaders(),
      });
    },
    enabled: computed(() => Number.isFinite(toValue(developerId)) && toValue(developerId) > 0),
    staleTime: 60_000,
    retry: false,
  });
};

export const downloadPresentationIcs = async (presentation: Pick<UpcomingPresentation, 'id' | 'title'>) => {
  const body = await $fetch<string>(`/api/developer-presentations/${encodeURIComponent(presentation.id)}/ics`, {
    responseType: 'text',
    headers: optionalAuthHeaders(),
  });
  const blob = new Blob([body], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${presentation.title.replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'presentation'}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
